import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { RedButton, WhiteButton } from '../../components/Button';
import PurchaseTotalTextInput from './PurchaseTotalTextInput';
import BodyText from '../../components/BodyText';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { Pages } from '../../App';
import { updateNewPurchasePageData } from '../../DumbleData';
import BigNumber from 'bignumber.js';

interface Props {
	taxStatus: string;
	taxRate: BigNumber;
	taxExemptSalesAllowed: boolean;
	maxTransactionItems: number;
	values: { key: number; value: string }[];
	setValues: React.Dispatch<React.SetStateAction<{ key: number; value: string }[]>>;
	isChecked: boolean;
	setIsChecked: React.Dispatch<React.SetStateAction<boolean>>;
	itemTotal: BigNumber;
	tax: BigNumber;
	amountDue: BigNumber;
	resetInfo: any;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const NewPurchaseContent = ({ values, setValues, itemTotal, tax, amountDue, isChecked, setIsChecked, taxStatus, maxTransactionItems, taxRate, setCurrentPage }: Props) => {
	const [error, setError] = useState<boolean>(false);
	const inputIncrement = useRef(4);

	// When updating a value in one of the transaction rows, update the list
	const handleInputChange = (index: number, value: string) => {
		const newValues = [...values];
		newValues[index].value = value;
		// If the last item is being edited, add another row (unless max number of items has been reached)
		if (newValues[newValues.length - 1].value !== '' && newValues.length < maxTransactionItems) {
			newValues.push({ key: inputIncrement.current, value: '' });
			inputIncrement.current += 1;
		}
		setValues(newValues);
	};

	// Reset the input fields to four blank rows
	const clearFields = () => {
		setValues([
			{ key: 0, value: '' },
			{ key: 1, value: '' },
			{ key: 2, value: '' },
			{ key: 3, value: '' },
		]);
		inputIncrement.current = 4;
	};

	// Navigate to next page, unless no items have been added
	const handleCheckout = () => {
		if (!amountDue.isZero()) {
			window._satellite.track('new-purchase-tax-checkbox', { taxExemptChecked: isChecked });
			window._satellite.track('new-purchase-checkout');
			updateNewPurchasePageData(itemTotal.toNumber(), tax.toNumber(), amountDue.toNumber());
			setError(false);
			setCurrentPage(Pages.PaymentMethod);
		} else {
			setError(true);
		}
	};

	return (
		<>
			<NewPurchasePage>
				<NewPurchasePageWrapper>
					<TopCTAText>Enter the purchase total. You can also enter the price for each item, and the amount due will be calculated below.</TopCTAText>
					{values.map((value, index) => (
						<PurchaseTotalTextInput index={index} key={value.key} value={value.value} handleInputChange={handleInputChange} />
					))}
					{!taxRate.isZero() && taxStatus === 'OF' && (
						<TaxExemptDiv>
							<CheckBox checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
							<CheckBoxText>This is a tax-exempt purchase by the school. I confirm this purchase is for the school using school funds and is qualified for exemption from sales tax.</CheckBoxText>
						</TaxExemptDiv>
					)}
					<CalculationsDiv>
						<RowItemDiv>
							<ItemLabel>Item Total</ItemLabel>
							<ItemValue>{formatCurrencyNumber(itemTotal.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Tax</ItemLabel>
							<ItemValue>{formatCurrencyNumber(tax.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Amount Due</ItemLabel>
							<ItemValue>{formatCurrencyNumber(amountDue.toNumber())}</ItemValue>
						</RowItemDiv>
						<StyledRedButton handleClick={handleCheckout}>CHECKOUT</StyledRedButton>
						{error && <StyledError>Please enter an item amount.</StyledError>}
					</CalculationsDiv>
					<StyledWhiteButton whiteBackground={false} handleClick={clearFields}>
						Clear
					</StyledWhiteButton>
				</NewPurchasePageWrapper>
			</NewPurchasePage>
		</>
	);
};

const NewPurchasePage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const NewPurchasePageWrapper = styled.div`
	display: flex;
	width: 280px;
	flex-direction: column;
	justify-content: center;
	margin: 32px 0 96px 0;
	gap: 16px;
`;

const TopCTAText = styled(BodyText)`
	text-align: left;
	font-weight: 500;
	margin: 0 0 -8px 0;
`;

const TaxExemptDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	align-self: stretch;
`;

const CheckBox = styled.input.attrs({ type: 'checkbox' })`
	box-sizing: border-box;
	width: 20px;
	height: 20px;
	margin: 0;
	border-radius: 4px;
	border: 1px solid #666;
	background: #fff;
`;

const CheckBoxText = styled(BodyText)`
	flex: 1 0 0;
	font-weight: 500;
	text-align: left;
	margin: 0;
`;

const CalculationsDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
	margin-top: 27px;
`;

const StyledError = styled.p`
	margin: 0;
	font-size: 13px;
	font-weight: 500;
	line-height: 15px;
	text-align: center;
	width: 100%;
	color: #e81111;
`;

const StyledWhiteButton = styled(WhiteButton)`
	width: 100%;
	padding: 0;
	outline: none;
	font-size: 13px;
	font-weight: 500;
	line-height: 15px;
	text-decoration-line: underline;
	text-transform: none;
	letter-spacing: 0;
`;

export default NewPurchaseContent;
